import React, { useMemo, useState } from 'react';
import Collapse from 'react-bootstrap/Collapse';

import Loader from 'components/atoms/loader';
import SummaryAccordionBlock from 'components/molecules/summary_accordion_block';
import { StrategicQuestion } from 'services/actions/ai_management_strategic_questions';
import { usePerguntasEstrategicas } from 'hooks/usePerguntasEstrategicas';

import iconStrategicQuestionsSummary from 'assets/images/icon_strategic_questions_summary.svg';
import { Container } from './styles';

// Ordem e rótulos dos grupos conforme o protótipo; tópico desconhecido vai para o fim.
const TOPICOS: { key: string; label: string }[] = [
  { key: 'proposta', label: 'Proposta' },
  { key: 'objeto', label: 'Objeto' },
  { key: 'habilitacao', label: 'Habilitação' },
  { key: 'pagamentos', label: 'Pagamentos' },
  { key: 'riscos', label: 'Riscos' },
  { key: 'contrato', label: 'Contrato' },
];

interface Grupo {
  key: string;
  label: string;
  perguntas: StrategicQuestion[];
}

const agruparPorTopico = (perguntas: StrategicQuestion[]): Grupo[] => {
  const conhecidos = TOPICOS.map(({ key, label }) => ({
    key,
    label,
    perguntas: perguntas.filter((pergunta) => pergunta.topico === key),
  }));
  const outros = perguntas.filter(
    (pergunta) => !TOPICOS.some(({ key }) => key === pergunta.topico),
  );

  return [...conhecidos, { key: 'outros', label: 'Outras', perguntas: outros }].filter(
    (grupo) => grupo.perguntas.length > 0,
  );
};

const Chevron: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" aria-hidden focusable="false">
    {expanded ? (
      <path fill="currentColor" d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z" />
    ) : (
      <path fill="currentColor" d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
    )}
  </svg>
);

interface PerguntaItemProps {
  pergunta: StrategicQuestion;
  expanded: boolean;
  timedOut: boolean;
  onToggle: () => void;
}

const PerguntaItem: React.FC<PerguntaItemProps> = ({ pergunta, expanded, timedOut, onToggle }) => {
  const panelId = `pergunta-estrategica-${pergunta.id.replace(/\W+/g, '-')}`;

  return (
    <div className="strategic-question">
      <button
        type="button"
        className="strategic-question-header"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        id={`${panelId}-toggle`}
      >
        <span className="strategic-question-title">{pergunta.titulo}</span>
        <Chevron expanded={expanded} />
      </button>
      <Collapse in={expanded}>
        <div id={panelId} role="region" aria-labelledby={`${panelId}-toggle`}>
          <div className="strategic-question-body">
            <div className="strategic-question-label">{pergunta.texto}</div>
            {pergunta.resposta === null ? (
              <div className="strategic-question-pending">
                {timedOut ? 'Resposta indisponível no momento.' : 'Gerando resposta…'}
              </div>
            ) : (
              <div className="strategic-question-answer">{pergunta.resposta}</div>
            )}
          </div>
        </div>
      </Collapse>
    </div>
  );
};

interface AiManagementStrategicQuestionsProps {
  biddingId: number;
}

const AiManagementStrategicQuestions: React.FC<AiManagementStrategicQuestionsProps> = ({
  biddingId,
}) => {
  const [open, setOpen] = useState(true);
  const [expandidas, setExpandidas] = useState<Set<string>>(() => new Set());
  const { data, loading, error, timedOut, retry } = usePerguntasEstrategicas(biddingId);

  const grupos = useMemo(() => agruparPorTopico(data?.perguntas ?? []), [data]);

  const togglePergunta = (id: string) =>
    setExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderBody = () => {
    if (loading) return <Loader />;

    if (error && !data) {
      return (
        <div className="strategic-questions-message">
          <p className="mb-2">Não foi possível carregar as perguntas estratégicas.</p>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={retry}>
            Tentar novamente
          </button>
        </div>
      );
    }

    if (timedOut && (data?.pendente || grupos.length === 0)) {
      return (
        <div className="strategic-questions-message">
          <p className="mb-2">As perguntas estratégicas estão demorando mais que o esperado.</p>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={retry}>
            Tentar novamente
          </button>
        </div>
      );
    }

    if (data?.indisponivel) {
      return (
        <p className="strategic-questions-message mb-0">
          O resumo do edital ainda não está pronto. As perguntas estratégicas aparecem aqui assim
          que ele for concluído.
        </p>
      );
    }

    if (data?.pendente || grupos.length === 0) {
      return (
        <p className="strategic-questions-message mb-0">
          Gerando as perguntas estratégicas desta licitação…
        </p>
      );
    }

    return grupos.map((grupo) => (
      <section key={grupo.key} className="strategic-questions-group">
        <h5 className="strategic-questions-group-title">{grupo.label}</h5>
        {grupo.perguntas.map((pergunta) => (
          <PerguntaItem
            key={pergunta.id}
            pergunta={pergunta}
            expanded={expandidas.has(pergunta.id)}
            timedOut={timedOut}
            onToggle={() => togglePergunta(pergunta.id)}
          />
        ))}
      </section>
    ));
  };

  return (
    <SummaryAccordionBlock
      open={open}
      onToggle={() => setOpen((prev) => !prev)}
      accordionPanelId="bidding-summary-panel-perguntas-estrategicas"
      title="Perguntas estratégicas"
      iconSrc={iconStrategicQuestionsSummary}
    >
      <Container>{renderBody()}</Container>
    </SummaryAccordionBlock>
  );
};

export default AiManagementStrategicQuestions;
